import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { setCredentials, logOut } from "../features/auth/authSlice";
import { API_ORIGIN } from "../Utils";

const BASE_URL = API_ORIGIN;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token =
      getState().auth.accessToken ||
      localStorage.getItem("accessToken") ||
      null;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result?.error?.status === 403) {
    try {
      const refreshToken = api.getState().auth.refreshToken;
      if (!refreshToken) {
        api.dispatch(logOut());
        return result;
      }

      const { data } = await axios.post(
        `${BASE_URL}/users/api/auth/refresh/`,
        {
          refreshToken,
        }
      );

      if (data?.accessToken) {
        const { accessToken, refreshToken: newRefreshToken, user } = data;
        api.dispatch(
          setCredentials({
            accessToken,
            refreshToken: newRefreshToken,
            user,
          })
        );

        result = await rawBaseQuery(args, api, extraOptions);
      } else {
        api.dispatch(logOut());
      }
    } catch (error) {
      api.dispatch(logOut());
    }
  }

  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: ["partners-view", "PartnerDashboard"],
  endpoints: (builder) => ({
    submitPartnerApplication: builder.mutation({
      query: (body) => ({
        url: "partners/api/partners/applications/",
        method: "POST",
        body,
      }),
    }),
    checkPartnerApplicationEmail: builder.query({
      query: (email) => ({
        url: "partners/api/partners/applications/check-email/",
        method: "GET",
        params: { email },
      }),
    }),
    capturePartnerInterest: builder.mutation({
      query: (body) => ({
        url: "partners/api/partners/interest/",
        method: "POST",
        body,
      }),
    }),
    getPartnerStatus: builder.query({
      query: () => ({
        url: "partners/api/partners/status/",
        method: "GET",
      }),
    }),
    getPartnerDiscount: builder.query({
      query: (discountCode) => ({
        url: `partners/api/partners/discount/${encodeURIComponent(discountCode)}/`,
        method: "GET",
      }),
    }),
    getPartnerDashboard: builder.query({
      query: () => ({
        url: "partners/api/partners/dashboard/",
        method: "GET",
      }),
      providesTags: ["PartnerDashboard"],
    }),
    getPartnerReferrals: builder.query({
      query: (params = {}) => ({
        url: "partners/api/partners/referrals/",
        method: "GET",
        params,
      }),
      providesTags: ["PartnerDashboard"],
    }),
    getPartnerPayouts: builder.query({
      query: (params = {}) => ({
        url: "partners/api/partners/payouts/",
        method: "GET",
        params,
      }),
      providesTags: ["PartnerDashboard"],
    }),
    getPartnerAnalytics: builder.query({
      query: (params = {}) => ({
        url: "partners/api/partners/analytics/",
        method: "GET",
        params,
      }),
      providesTags: ["PartnerDashboard"],
    }),
    getVideoIntroData: builder.query({
      query: () => ({
        url: "/viewerlounge/api/video-introduction",
        method: "GET",
      }),
      providesTags: ["partners-view"],
    }),
    getShowreel: builder.query({
      query: () => ({
        url: "/viewerlounge/ShowReel/463",
        method: "GET",
      }),
      providesTags: ["partners-view"],
    }),
    getBrowseLinks: builder.query({
      query: (url = "/viewerlounge/GetBrowseLink") => ({
        url,
        method: "GET",
      }),
      providesTags: ["partners-view"],
    }),
    getMyLinks: builder.query({
      query: (url = "/viewerlounge/getMyPost/All/Link") => ({
        url,
        method: "GET",
      }),
      providesTags: ["partners-view"],
    }),
    checkUserAccess: builder.query({
      query: ({ id, flag }) => ({
        url: `/pay/checkuseraccess/${id}/${flag}`,
        method: "GET",
      }),
      providesTags: ["partners-view"],
    }),
  }),
});

export const {
  useGetVideoIntroDataQuery,
  useGetShowreelQuery,
  useGetBrowseLinksQuery,
  useGetMyLinksQuery,
  useCheckUserAccessQuery,
  useSubmitPartnerApplicationMutation,
  useLazyCheckPartnerApplicationEmailQuery,
  useCapturePartnerInterestMutation,
  useLazyGetPartnerDiscountQuery,
  useGetPartnerDashboardQuery,
  useGetPartnerReferralsQuery,
  useGetPartnerPayoutsQuery,
  useGetPartnerAnalyticsQuery,
  useGetPartnerStatusQuery,
} = apiSlice;

